import { useEffect, useReducer, useState } from "react";
import type { EventSectionView, EventView } from "common";
import EventSectionCard from "./event-section-card";
import type { EventPathEntry } from "./event-path";
import {
  eventErrorMessage,
  eventErrorStatus,
  useSubmitVmetaEvent,
  useVmetaEvent,
} from "./events.api";
import {
  buildSubmitBody,
  defaultsOf,
  eventFormReducer,
  initialFormState,
  validateForSubmit,
  visibleSections,
  type EventFormState,
} from "./event-form-state";

const EMPTY: EventFormState = {
  version: null,
  dropdownChoice: null,
  sections: {},
  sharedKeys: [],
};

interface Props {
  canvasId: string;
  eventId: string;
  path: EventPathEntry[];
}

/** A section may repeat only when it is multiple and, if shared, so is its owner. */
const canAddAnother = (view: EventView, section: EventSectionView): boolean => {
  if (!section.multiple) return false;
  if (!section.sharesRecordWith) return true;
  const owner = view.sections.find(s => s.key === section.sharesRecordWith);
  return !!owner?.multiple;
};

const EventRunner = ({ canvasId, eventId, path }: Props) => {
  const {
    data: view,
    isLoading,
    error,
    refetch,
  } = useVmetaEvent(canvasId, eventId);
  const submit = useSubmitVmetaEvent();
  const [state, dispatch] = useReducer(eventFormReducer, EMPTY);
  const [message, setMessage] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [saving, setSaving] = useState(false);

  // A new view (first load or a reload) rebuilds the form. The path is read once per view.
  useEffect(() => {
    if (!view) return;
    dispatch({ type: "reset", state: initialFormState(view, path) });
    setMessage(null);
    setStale(false);
  }, [view]);

  if (isLoading) {
    return <p className="p-4 text-sm text-gray-500">Loading the event form.</p>;
  }
  if (!view) {
    return (
      <p className="p-4 text-sm text-gray-700">
        {!error || eventErrorStatus(error) === 404
          ? "This event form is not available."
          : eventErrorMessage(error, "This event form could not be loaded.")}
      </p>
    );
  }
  // The form state follows the view one effect later.
  if (state.version === null) return null;

  const onSave = async () => {
    setMessage(null);
    const errors = validateForSubmit(view, state);
    dispatch({ type: "setClientErrors", errors });
    if (Object.values(errors).some(list => list.some(Boolean))) {
      setMessage("Check the highlighted fields.");
      return;
    }
    const body = buildSubmitBody(view, state);
    if (!body) {
      setMessage("Nothing to save.");
      return;
    }
    setSaving(true);
    try {
      const results = await submit.mutateAsync({ canvasId, eventId, body });
      dispatch({ type: "applyResults", results });
      setMessage(
        results.every(r => r.status === "saved")
          ? "All sections saved."
          : "Saving stopped at a failed section. Fix it and save again."
      );
    } catch (e) {
      if (eventErrorStatus(e) === 409) setStale(true);
      setMessage(eventErrorMessage(e, "The form could not be saved."));
    } finally {
      setSaving(false);
    }
  };

  const onReload = async () => {
    const fresh = await refetch();
    if (fresh.data) {
      dispatch({ type: "reset", state: initialFormState(fresh.data, path) });
      setMessage(null);
      setStale(false);
    }
  };

  const dropdownLabelOf = (collectionId: string) =>
    view.sections.find(s => s.collectionId === collectionId)?.collectionLabel ??
    collectionId;

  return (
    <div className="flex flex-col gap-4 p-4">
      <h2 className="text-lg font-semibold text-gray-900">{view.name}</h2>
      {error && (
        <p role="alert" className="text-sm text-red-600">
          {eventErrorMessage(error, "This event form could not be refreshed.")}
        </p>
      )}
      {view.warnings.length > 0 && (
        <p className="text-sm text-amber-700">
          Parts of this form could not be shown.
        </p>
      )}
      {view.dropdown && (
        <div className="flex max-w-sm flex-col gap-1">
          <label
            htmlFor="event-dropdown"
            className="text-sm font-medium text-gray-700">
            {view.dropdown.label}
          </label>
          <select
            id="event-dropdown"
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm"
            value={state.dropdownChoice ?? ""}
            onChange={e =>
              dispatch({
                type: "chooseDropdown",
                collectionId: e.target.value || null,
              })
            }>
            <option value="" />
            {view.dropdown.collectionIds.map(id => (
              <option key={id} value={id}>
                {dropdownLabelOf(id)}
              </option>
            ))}
          </select>
        </div>
      )}
      {visibleSections(view, state).map(section => {
        const list = state.sections[section.key] ?? [];
        return (
          <div key={section.key} className="flex flex-col gap-3">
            {list.map((_, i) => (
              <EventSectionCard
                key={`${section.key}-${i}`}
                view={view}
                state={state}
                section={section}
                instance={i}
                dispatch={dispatch}
                disabled={saving}
              />
            ))}
            {canAddAnother(view, section) && (
              <div className="flex gap-2">
                <button
                  type="button"
                  className="text-sm font-medium text-indigo-600"
                  onClick={() =>
                    dispatch({
                      type: "addInstance",
                      key: section.key,
                      values: defaultsOf(section),
                    })
                  }>
                  Add another
                </button>
                {list.length > 1 && !list[list.length - 1].recordId && (
                  <button
                    type="button"
                    className="text-sm font-medium text-gray-600"
                    onClick={() =>
                      dispatch({ type: "removeInstance", key: section.key })
                    }>
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          disabled={saving}
          onClick={onSave}>
          Save
        </button>
        {stale && (
          <button
            type="button"
            className="rounded-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300"
            onClick={onReload}>
            Reload
          </button>
        )}
        {message && <p className="text-sm text-gray-700">{message}</p>}
      </div>
    </div>
  );
};

export default EventRunner;
